import {getTestProps} from '@benchling/js-test-utils';
import {Color} from '@benchling/nucleus-ui';
import {Flex, Text} from '@mantine/core';
import React from 'react';

import {assert} from 'Asserts';

interface LegendItemProps {
  description: string;
  color: string;
  label?: string;
  backgroundColor?: string;
  symbol?: string;
}

export const CENTER_COLOR = Color.GREEN_600;
export const CENTER_BACKGROUND_COLOR = Color.GREEN_200;
export const MODULE_COLOR = Color.TEAL_600;
export const MODULE_BACKGROUND_COLOR = Color.TEAL_150;
export const SYMBOL_COLOR = Color.GRAY_500;
export const CHAIN_SEPARATOR_COLOR = Color.RED_500;

/**
 * Display a formatted legend item for a VERITAS name component.
 * @param description - The description of the VERTIAS component.
 * @param color - The color of the VERITAS component, matching syntax highlighting for this component.
 * @param backgroundColor - The background color of the VERITAS component, matching syntax highlighting for this component.
 * @param label - The label of the VERITAS component. Must be provided if symbol is not provided.
 * @param symbol - The symbol of the VERITAS component. Must be provided if label is not provided.
 */
function LegendItem({description, color, backgroundColor, label, symbol}: LegendItemProps): JSX.Element {
  assert(label != null || symbol != null, 'Either label or symbol must be provided');
  return (
    <Flex align="center" gap={8}>
      {symbol != null ? (
        <Text span={true} fw="bold" fz="sm" lh={1} w={18} ta="center" color={color}>
          {symbol}
        </Text>
      ) : (
        <Text
          span={true}
          fw="bold"
          fz="sm"
          lh={1.2}
          px={4}
          py={2}
          c={color}
          sx={{
            borderRadius: 4,
            backgroundColor: backgroundColor ?? 'inherit',
          }}
        >
          {label}
        </Text>
      )}
      <Text span={true} color={Color.GRAY_700} fz="sm">
        {description}
      </Text>
    </Flex>
  );
}

function VeritasSyntaxKey(): JSX.Element {
  return (
    <Flex
      align="flex-start"
      direction="column"
      gap={6}
      p={2}
      {...getTestProps({component: 'VeritasSyntaxKey', element: 'root'})}
    >
      <Text span={true} color={Color.GRAY_600} fz="xs" fw={600}>
        Highlighting legend
      </Text>
      <LegendItem
        color={MODULE_COLOR}
        backgroundColor={MODULE_BACKGROUND_COLOR}
        label="Module"
        description="Basic components (VH, VL)"
      />
      <LegendItem
        color={CENTER_COLOR}
        backgroundColor={CENTER_BACKGROUND_COLOR}
        label="Center"
        description="Multimerization centers (IgG, FC)"
      />
      <LegendItem color={SYMBOL_COLOR} symbol="-" description="Same chain connector" />
      <LegendItem color={CHAIN_SEPARATOR_COLOR} symbol="*" description="Different chains separator" />
      <LegendItem color={SYMBOL_COLOR} symbol="[]" description="Asymmetric appendages" />
      <LegendItem color={SYMBOL_COLOR} symbol=":" description="Non-covalent interaction" />
      <LegendItem color={SYMBOL_COLOR} symbol="!" description="Reverse previous module domains" />
    </Flex>
  );
}

export default VeritasSyntaxKey;
